import { InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { LightTooltip } from 'components/tooltips/LightTooltip';
import { groupHourlyRates, useHourlyRates } from 'hooks/useHourlyRates';
import { formatRateRange } from 'pages/pricelists/rateRange';
import { useMemo, useState } from 'react';
import { useFindMatterRatesQuery, useLazyFindMatterRatesQuery } from 'store/api/matters';
import {
    useFindPricelistRatesQuery,
    useGetPricelistByIdQuery,
    useLazyFindPricelistRatesQuery,
    useLazyGetPricelistByIdQuery,
} from 'store/api/pricelists';
import { JobRoleGroupType } from 'types/jobRoles';
import {
    CalculatedPricelist,
    EmployeeRate,
    Fetchable,
    HourlyRate,
    MatterPricelist,
    Pricelist,
    ScopeType,
} from 'types/pricelists';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { CopyHourlyRatesToClipboard } from './CopyHourlyRatesToClipboard';
import { HourlyRateDetailsCard } from './HourlyRateDetailsCard';
import { LabelButtons } from './buttons/LabelButtons';
import {
    employeeRatesOfCaseList,
    employeeRatesOfRateRows,
    inForcePeriodOf,
    isEmployeePriced,
    pricedEmployeeRows,
    roleRatesOf,
} from './modal/employeePricing';
import { inForceListOn } from './modal/matterPricelistEditorUtils';
import { useToday } from './modal/useEmployeePricing';
import { ClipboardRateRow, EMPLOYEE_CLIPBOARD_GROUP, employeeClipboardGroups } from './util';

interface Props {
    pricelist: Fetchable;
    simpleView?: boolean;
    whiteSpace?: 'wrap' | 'nowrap';
}

/**
 * A price-list card whose rows are fetched on demand: the range and average come with the list, the rows
 * behind the info icon and the copy icon are read once the pointer or focus reaches the card. A copy pressed
 * before the rows have arrived waits for them rather than copying an empty table.
 */
export const PricelistDetailsFetchingCard = ({ pricelist, simpleView, whiteSpace = 'wrap' }: Props): JSX.Element => {
    const isMatterPricelist = pricelist.scopeTypeCode === ScopeType.MATTER;
    // An employee-priced list's people come with its detail, in periods; it has no role rows to read.
    const isEmployeeList = !isMatterPricelist && isEmployeePriced(pricelist);
    const byMatter = isMatterPricelist && pricelist.matterId != null;
    // A case's employee-priced list holds its people as the case list's personal rows.
    const isEmployeeCaseList = byMatter && isEmployeePriced(pricelist);
    const today = useToday();

    // Only whether the rows are wanted is state; the ids follow the props, so a row that is handed another
    // list fetches that list's rows rather than keeping the first one's.
    const [ratesWanted, setRatesWanted] = useState(false);
    const matterId = ratesWanted && byMatter ? String(pricelist.matterId) : undefined;
    const pricelistId = ratesWanted && !byMatter && !isEmployeeList ? String(pricelist.id) : undefined;
    const detailId = ratesWanted && isEmployeeList ? pricelist.id : undefined;
    const { data: pricelistRates } = useFindPricelistRatesQuery(pricelistId ?? skipToken);
    const { data: matterPricelists } = useFindMatterRatesQuery(matterId ?? skipToken);
    const { data: pricelistDetail } = useGetPricelistByIdQuery(detailId ?? skipToken);
    const [fetchPricelistRates] = useLazyFindPricelistRatesQuery();
    const [fetchMatterRates] = useLazyFindMatterRatesQuery();
    const [fetchPricelistDetail] = useLazyGetPricelistByIdQuery();
    const employeeRatesOf = (detail: Pricelist) => pricedEmployeeRows(inForcePeriodOf(detail.periods, today)?.rates);
    // A case card stands for one of the case's lists, so the tooltip shows that list's rows, not the whole case's.
    const matterPricelistId = (pricelist as CalculatedPricelist).matterPricelistId;
    const thisList = (lists: MatterPricelist[]): MatterPricelist | undefined =>
        isEmployeeCaseList && matterPricelistId == null
            ? inForceListOn(lists, today)
            : lists.find((list) => matterPricelistId == null || list.id === matterPricelistId);
    const ratesOfThisList = (lists: MatterPricelist[]): HourlyRate[] => thisList(lists)?.rates ?? [];
    const peopleOfThisList = (lists: MatterPricelist[]): EmployeeRate[] =>
        employeeRatesOfCaseList(thisList(lists)?.personalRates);
    // A list read by its rates alone, of a kind the card was not told, is employee-priced when its rows are people.
    const ratePeople = pricelistRates ? employeeRatesOfRateRows(pricelistRates) : undefined;
    const employeeRates = isEmployeeList
        ? pricelistDetail && employeeRatesOf(pricelistDetail)
        : isEmployeeCaseList
          ? matterPricelists && peopleOfThisList(matterPricelists)
          : ratePeople?.length
            ? ratePeople
            : undefined;
    const showsPeople = isEmployeeList || isEmployeeCaseList || !!ratePeople?.length;
    const rates = isMatterPricelist
        ? matterPricelists && ratesOfThisList(matterPricelists)
        : pricelistRates && roleRatesOf(pricelistRates);

    const { hourlyRateDetails } = useHourlyRates(rates);
    const { classifiers } = useClassifiers({ codes: ['JOB_ROLE_GROUP_TYPE'] });

    const groupTitles = useMemo(
        () => ({
            [JobRoleGroupType.LEGAL]: getClassifierText(classifiers?.['JOB_ROLE_GROUP_TYPE'], JobRoleGroupType.LEGAL),
            [JobRoleGroupType.ADMIN]: getClassifierText(classifiers?.['JOB_ROLE_GROUP_TYPE'], JobRoleGroupType.ADMIN),
            [EMPLOYEE_CLIPBOARD_GROUP]: t('pricelists.employee.clipboard-group'),
        }),
        [classifiers]
    );

    const startFetchingRates = () => setRatesWanted(true);

    const loadEmployeeGroups = async () =>
        employeeClipboardGroups(
            isEmployeeCaseList
                ? peopleOfThisList(await fetchMatterRates(String(pricelist.matterId), true).unwrap())
                : employeeRatesOf(await fetchPricelistDetail(pricelist.id, true).unwrap())
        );

    const loadRates = async (): Promise<Record<string, HourlyRate[]>> => {
        if (byMatter) {
            const lists = await fetchMatterRates(String(pricelist.matterId), true).unwrap();
            return groupHourlyRates(ratesOfThisList(lists));
        }
        return groupHourlyRates(roleRatesOf(await fetchPricelistRates(String(pricelist.id), true).unwrap()));
    };

    // A role list's read has no people, so its copy adds no group.
    const ratePeopleGroups = (people: EmployeeRate[]): Record<string, ClipboardRateRow[]> =>
        people.length ? employeeClipboardGroups(people) : {};
    const loadRatePeopleGroups = async () =>
        ratePeopleGroups(employeeRatesOfRateRows(await fetchPricelistRates(String(pricelist.id), true).unwrap()));

    return (
        <div
            className={cn(
                'text-grey-600 flex flex-col justify-start',
                whiteSpace === 'nowrap' ? 'whitespace-nowrap' : 'whitespace-normal'
            )}
            onPointerEnter={startFetchingRates}
            onFocus={startFetchingRates}
        >
            {pricelist && !simpleView && (
                <LabelButtons labels={pricelist.labelsList || []} pricingKind={pricelist.pricingKind} />
            )}
            {pricelist && !simpleView && (
                <div className="mr-2 overflow-hidden font-medium text-ellipsis">{pricelist.name}</div>
            )}
            <LightTooltip
                onOpen={startFetchingRates}
                title={
                    <HourlyRateDetailsCard
                        data={hourlyRateDetails}
                        employeeRates={showsPeople ? (employeeRates ?? []) : undefined}
                        fontSize="0.8rem"
                    />
                }
            >
                {/* Unbreakable; in a managed table cell of fixed width the range gives way to an ellipsis
                    while the two icons keep their size, so they stay visible and pressable. */}
                <div className="hover:text-link flex min-w-0 cursor-default items-center whitespace-nowrap hover:underline">
                    <span className="truncate">
                        {formatRateRange(
                            pricelist?.minHourlyPrice ?? 0,
                            pricelist?.maxHourlyPrice ?? 0,
                            pricelist?.avgHourlyPrice,
                            pricelist.currencyTypeCode
                        )}
                    </span>
                    <InfoOutlinedIcon className="text-grey-400 ml-px h-[1.2rem] shrink-0" />
                    {isEmployeeList || isEmployeeCaseList ? (
                        <CopyHourlyRatesToClipboard
                            data={{}}
                            extraGroups={employeeRates ? employeeClipboardGroups(employeeRates) : undefined}
                            loadExtraGroups={loadEmployeeGroups}
                            groupTitles={groupTitles}
                        />
                    ) : (
                        <CopyHourlyRatesToClipboard
                            data={rates ? hourlyRateDetails : undefined}
                            loadData={loadRates}
                            extraGroups={byMatter || !ratePeople ? undefined : ratePeopleGroups(ratePeople)}
                            loadExtraGroups={byMatter ? undefined : loadRatePeopleGroups}
                            groupTitles={groupTitles}
                        />
                    )}
                </div>
            </LightTooltip>
        </div>
    );
};
